import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { WaitingAlert, DangerAlert, Table, CtaBtn } from '@tmapy/style-guide';

import { ExportToolListItem } from './ExportToolListItem';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';

import { exportToolMessages as msg } from './exportToolMessages';
import { QUERY_STATE } from '../../constants';
import { useHistoryAction } from '../../hooks';

import { useExportToolsListResource } from '../../openapi/operations/exportToolsList';
import { useFormatsListResource } from '../../openapi/operations/formatsList';
import { useGpTasksListResource } from '../../openapi/operations/gpTasksList';

export const ExportToolList: React.FC = () => {
  const navigationType = useNavigationType();

  const { queryState, data, fetchMore } = useExportToolsListResource(
    {},
    navigationType !== 'PUSH',
  );

  const formatsResource = useFormatsListResource({}, navigationType !== 'PUSH');
  const gpTasksResource = useGpTasksListResource({}, navigationType !== 'PUSH');

  const exportToolList = data?.results ?? [];
  const exportToolCount = data?.count;

  // mapování format url -> název (SHP, DGN, …)
  const formatNameByUrl = useMemo(() => {
    const map: Record<string, string> = {};
    for (const format of formatsResource.data?.results ?? []) {
      map[format.url] = format.name;
    }
    return map;
  }, [formatsResource.data]);

  // mapování gp_task id -> název
  const gpTaskNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const task of gpTasksResource.data?.results ?? []) {
      map[task.id] = task.name;
    }
    return map;
  }, [gpTasksResource.data]);

  const handleNewExportToolBtnClick = useHistoryAction('vydejNewExportTool');

  const handleNextExportToolsBtnClick = useCallback(() => {
    if (data?.next) {
      fetchMore(data.next);
    }
  }, [fetchMore, data]);

  return (
    <MainContent width='fit' scrollRestorationPrefix='exporttoollist'>
      <article className='sg-u-vs-2'>
        <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
          <PageTitle>
            <FormattedMessage {...msg.exportToolListPageTitle} />
          </PageTitle>
          <span className='sg-a-f-a' />
          <CtaBtn onClick={handleNewExportToolBtnClick} size="sml">
            <FormattedMessage {...msg.exportToolListBtnNew} />
          </CtaBtn>
        </header>
        {queryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...msg.exportToolListStateWaiting} />
          </WaitingAlert>
        )}
        {queryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...msg.exportToolListStateFail} />
          </DangerAlert>
        )}
        {exportToolList.length > 0 && (
          <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
            <div className='sg-a-mx--2'>
              <Table isClickable>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...msg.format} />
                    </th>
                    <th>
                      <FormattedMessage {...msg.gpTask} />
                    </th>
                    <th>
                      <FormattedMessage {...msg.toolName} />
                    </th>
                    <th className='sg-a-ta-c'>
                      <FormattedMessage {...msg.isDefault} />
                    </th>
                    <th className='sg-a-ta-c'>
                      <FormattedMessage {...msg.isPackage} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exportToolList.map((exportToolItem) => (
                    <ExportToolListItem
                      key={exportToolItem.id}
                      {...exportToolItem}
                      formatName={formatNameByUrl[exportToolItem.format]}
                      gpTaskName={
                        exportToolItem.gp_task != null
                          ? gpTaskNameById[exportToolItem.gp_task]
                          : undefined
                      }
                    />
                  ))}
                </tbody>
              </Table>
            </div>
            <LoadNextItemsBar
              count={exportToolList.length}
              total={exportToolCount!}
              queryState={queryState}
              onNextBtnClick={handleNextExportToolsBtnClick}
            />
          </div>
        )}
      </article>
    </MainContent>
  );
};
